import moment from "moment";
import { FixtureResponse } from "../models/api";
import { MatchTimes, Team, TeamFixture } from "../models";
import { provisionalBonusForFixture } from "./bonus";

/** How long after kick-off a match is treated as live for refresh purposes. */
export const MATCH_WINDOW_SECONDS = 7200;

export type FixtureSummary = {
	/** One entry per Premier League team per fixture, used to look up a player's games. */
	teamFixtures: TeamFixture[];
	/** Kick-off windows for fixtures that have not finished, deduplicated by kick-off. */
	matchTimes: MatchTimes[];
};

export const matchReportFor = (f: FixtureResponse, teams: Team[]): string => {
	if (!f.started || teams.length === 0) {
		return "";
	}

	const homeTeam = teams.find((x) => x.id === f.team_h);
	const awayTeam = teams.find((x) => x.id === f.team_a);

	let matchReport = `${homeTeam?.name} (${f.team_h_score}) - ${awayTeam?.name} (${f.team_a_score})`;

	if (!f.finished_provisional) {
		matchReport += " '" + f.minutes;
	}

	return matchReport;
};

export const shortReportFor = (f: FixtureResponse, teams: Team[]): string => {
	const home = teams.find((x) => x.id === f.team_h)?.shortName ?? "?";
	const away = teams.find((x) => x.id === f.team_a)?.shortName ?? "?";
	return f.started ? `${home} ${f.team_h_score}–${f.team_a_score} ${away}` : `${home} v ${away}`;
};

export const summariseFixtures = (fixtures: FixtureResponse[], teams: Team[]): FixtureSummary => {
	const teamFixtures: TeamFixture[] = [];
	let matchTimes: MatchTimes[] = [];

	fixtures.forEach((f) => {
		const matchStart = moment(f.kickoff_time).unix();

		if (!f.finished) {
			matchTimes.push({ start: matchStart, end: matchStart + MATCH_WINDOW_SECONDS });
		}

		const matchInfo = {
			fixtureId: f.id,
			started: f.started,
			finished: f.finished_provisional,
			minutes: f.minutes,
			bps: provisionalBonusForFixture(f.stats),
			matchReport: matchReportFor(f, teams),
			shortReport: shortReportFor(f, teams),
			kickoff: f.kickoff_time,
		};

		teamFixtures.push({ id: f.team_a, ...matchInfo });
		teamFixtures.push({ id: f.team_h, ...matchInfo });
	});

	matchTimes = matchTimes.reduce((arr: MatchTimes[], m: MatchTimes) => {
		if (!arr.some((x) => x.start === m.start)) {
			arr.push(m);
		}
		return arr;
	}, []);

	return { teamFixtures, matchTimes };
};

export const isInsideMatchWindow = (matchTimes: MatchTimes[], unixNow: number): boolean => matchTimes.some((x) => unixNow >= x.start && unixNow <= x.end);
